import Cookies from 'js-cookie';

const TOKEN_KEY = 'access_token';
const USER_DATA_KEY = 'user_data';
const TOKEN_EXPIRY_DAYS = 7;

export interface StoredUserData {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

export const setToken = (token: string): void => {
  Cookies.set(TOKEN_KEY, token, {
    expires: TOKEN_EXPIRY_DAYS,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
    path: '/',
  });

  // Fall back to localStorage if cookies fail (e.g., browser restrictions)
  if (!Cookies.get(TOKEN_KEY)) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

export const getToken = (): string | undefined => {
  // Try cookie first, fall back to localStorage
  return Cookies.get(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || undefined;
};

export const removeToken = (): void => {
  Cookies.remove(TOKEN_KEY, { path: '/' });
  localStorage.removeItem(TOKEN_KEY);
};

export const setUserData = (userData: StoredUserData): void => {
  const jsonData = JSON.stringify(userData);
  Cookies.set(USER_DATA_KEY, jsonData, {
    expires: TOKEN_EXPIRY_DAYS,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
    path: '/',
  });

  // Fall back to localStorage if cookies fail (e.g., browser restrictions)
  if (!Cookies.get(USER_DATA_KEY)) {
    localStorage.setItem(USER_DATA_KEY, jsonData);
  }
};

export const getUserData = (): StoredUserData | null => {
  // Try cookie first, fall back to localStorage
  const data = Cookies.get(USER_DATA_KEY) || localStorage.getItem(USER_DATA_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

export const removeUserData = (): void => {
  Cookies.remove(USER_DATA_KEY, { path: '/' });
  localStorage.removeItem(USER_DATA_KEY);
};

export const isAuthenticated = (): boolean => {
  return !!getToken();
};

export const isAdmin = (): boolean => {
  const userData = getUserData();
  return userData?.roles?.includes('admin') || false;
};

// Theme mode persistence (365-day expiry - UI preference, not auth)
const THEME_MODE_KEY = 'theme_mode';

export const setThemeMode = (mode: 'light' | 'dark'): void => {
  Cookies.set(THEME_MODE_KEY, mode, {
    expires: 365,
    path: '/',
  });
  if (!Cookies.get(THEME_MODE_KEY)) {
    localStorage.setItem(THEME_MODE_KEY, mode);
  }
};

export const getThemeMode = (): 'light' | 'dark' => {
  const mode = Cookies.get(THEME_MODE_KEY) || localStorage.getItem(THEME_MODE_KEY);
  return mode === 'dark' ? 'dark' : 'light';
};
